import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type SafeLinkToken =
  | { kind: 'text'; text: string }
  | { kind: 'link'; label: string; href: string; hostname: string; showHostname: boolean };

export interface SafeLinkPreview {
  href: string;
  kicker: string;
  title: string;
  displayUrl: string;
}

export const SAFE_LINK_LIMIT = 20;
const MAX_PREVIEWS = 1;
const MAX_URL_LENGTH = 2_048;
const BIDI_CONTROL_PATTERN = /[\u061c\u200e\u200f\u202a-\u202e\u2066-\u2069]/u;
const GITHUB_REPOSITORY_PATTERN = /^[A-Za-z0-9_-]+\/[A-Za-z0-9_.-]+$/u;
const linkPattern = /\[([^\]\r\n]{1,120})\]\((https:\/\/[^\s<>"')]+)\)|(https:\/\/[^\s<>"']+)/giu;

function appendText(tokens: SafeLinkToken[], text: string): void {
  if (!text) return;
  const previous = tokens.at(-1);
  if (previous?.kind === 'text') previous.text += text;
  else tokens.push({ kind: 'text', text });
}

function isInsideMarkupLookingText(text: string, index: number): boolean {
  return text.lastIndexOf('<', index) > text.lastIndexOf('>', index);
}

function withoutTrailingPunctuation(raw: string): string {
  let value = raw;
  while (/[.,;:!?]$/u.test(value)) value = value.slice(0, -1);
  while (
    value.endsWith(')') &&
    (value.match(/\)/gu)?.length ?? 0) > (value.match(/\(/gu)?.length ?? 0)
  )
    value = value.slice(0, -1);
  return value;
}

function safeHttpsUrl(raw: string): URL | null {
  if (raw.length > MAX_URL_LENGTH) return null;
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== 'https:' || parsed.username || parsed.password || !parsed.hostname)
      return null;
    return parsed;
  } catch {
    return null;
  }
}

function rawAuthority(raw: string): string {
  return raw.slice('https://'.length).split(/[/?#]/u, 1)[0];
}

export function safeLinkTokens(
  text: string,
  maximumLinks = SAFE_LINK_LIMIT,
): readonly SafeLinkToken[] {
  const tokens: SafeLinkToken[] = [];
  let cursor = 0;
  let linkCount = 0;
  for (const match of text.matchAll(linkPattern)) {
    const index = match.index;
    appendText(tokens, text.slice(cursor, index));
    const markdownLabel = match[1];
    const matchedUrl = match[2] ?? match[3];
    const candidate = markdownLabel ? matchedUrl : withoutTrailingPunctuation(matchedUrl);
    const parsed = safeHttpsUrl(candidate);
    if (
      linkCount >= Math.max(0, maximumLinks) ||
      !parsed ||
      BIDI_CONTROL_PATTERN.test(markdownLabel ?? candidate) ||
      isInsideMarkupLookingText(text, index)
    ) {
      appendText(tokens, match[0]);
    } else {
      const href = parsed.href;
      const rawDestinationDiffers =
        rawAuthority(candidate).toLowerCase() !== parsed.host.toLowerCase();
      tokens.push({
        kind: 'link',
        label: markdownLabel ?? (rawDestinationDiffers ? href : candidate),
        href,
        hostname: parsed.host,
        showHostname: !!markdownLabel,
      });
      linkCount++;
      if (!markdownLabel) appendText(tokens, matchedUrl.slice(candidate.length));
    }
    cursor = index + match[0].length;
  }
  appendText(tokens, text.slice(cursor));
  return tokens;
}

export function safeGithubPullRequestToken(
  repository: string,
  rawUrl: string | null,
  label?: string,
  expectedNumber?: number,
): Extract<SafeLinkToken, { kind: 'link' }> | null {
  if (
    !rawUrl ||
    !GITHUB_REPOSITORY_PATTERN.test(repository) ||
    ['.', '..'].includes(repository.split('/')[1]) ||
    (label !== undefined && BIDI_CONTROL_PATTERN.test(label))
  )
    return null;
  const parsed = safeHttpsUrl(rawUrl);
  if (
    !parsed ||
    parsed.origin !== 'https://github.com' ||
    parsed.port ||
    parsed.search ||
    parsed.hash
  )
    return null;
  const segments = parsed.pathname.split('/').filter(Boolean);
  const numberText = segments[3] ?? '';
  if (
    segments.length !== 4 ||
    `${segments[0]}/${segments[1]}` !== repository ||
    segments[2] !== 'pull' ||
    !/^[1-9][0-9]*$/u.test(numberText)
  )
    return null;
  const pullRequestNumber = Number(numberText);
  if (
    !Number.isSafeInteger(pullRequestNumber) ||
    (expectedNumber !== undefined && pullRequestNumber !== expectedNumber)
  )
    return null;
  const href = `https://github.com/${repository}/pull/${pullRequestNumber}`;
  if (rawUrl !== href || parsed.href !== href) return null;
  return {
    kind: 'link',
    label: label ?? href,
    href,
    hostname: parsed.host,
    showHostname: label !== undefined,
  };
}

function previewFor(href: string): SafeLinkPreview {
  const url = new URL(href);
  const segments = url.pathname.split('/').filter(Boolean);
  if (url.origin === 'https://github.com' && segments.length >= 2) {
    const repository = `${segments[0]}/${segments[1]}`;
    if (segments[2] === 'pull' && /^\d+$/u.test(segments[3] ?? ''))
      return {
        href,
        kicker: 'GitHub',
        title: `${repository} · Pull request #${segments[3]}`,
        displayUrl: url.host + url.pathname,
      };
    if (segments[2] === 'actions' && segments[3] === 'runs' && /^\d+$/u.test(segments[4] ?? ''))
      return {
        href,
        kicker: 'GitHub',
        title: `${repository} · Actions run ${segments[4]}`,
        displayUrl: url.host + url.pathname,
      };
    if (segments[2] === 'commit' && /^[0-9a-f]{7,40}$/iu.test(segments[3] ?? ''))
      return {
        href,
        kicker: 'GitHub',
        title: `${repository} · Commit ${segments[3].slice(0, 12)}`,
        displayUrl: url.host + url.pathname,
      };
  }
  const path = `${url.pathname}${url.search}`;
  return {
    href,
    kicker: url.host,
    title: path === '/' ? 'Open website' : path.slice(0, 100),
    displayUrl: `${url.host}${path}`.slice(0, 140),
  };
}

export function safeLinkPreviews(tokens: readonly SafeLinkToken[]): readonly SafeLinkPreview[] {
  const seen = new Set<string>();
  return tokens
    .filter((token): token is Extract<SafeLinkToken, { kind: 'link' }> => token.kind === 'link')
    .filter((token) => {
      if (seen.has(token.href)) return false;
      seen.add(token.href);
      return true;
    })
    .slice(0, MAX_PREVIEWS)
    .map((token) => previewFor(token.href));
}

@Component({
  selector: 'app-safe-link-text',
  standalone: true,
  templateUrl: './safe-link-text.component.html',
  styleUrl: './safe-link-text.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SafeLinkTextComponent {
  readonly text = input('');
  readonly providedTokens = input<readonly SafeLinkToken[] | null>(null, { alias: 'tokens' });
  readonly showText = input(true);
  readonly showPreview = input(false);
  readonly renderedTokens = computed(() => this.providedTokens() ?? safeLinkTokens(this.text()));
  readonly previews = computed(() =>
    this.showPreview() ? safeLinkPreviews(this.renderedTokens()) : [],
  );
}
